import React, { useMemo, useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { StatCard } from '../../shared/StatCard';
import { DataTable, Column } from '../shared/DataTable';
import { MasteryBar } from '../shared/MasteryBar';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Badge } from '../../../components/ui/Badge';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { CheckCircle2, XCircle, Search, Filter, BookOpen, FileText, X, Lightbulb, AlertTriangle } from 'lucide-react';
import { TAXONOMY, QUESTIONS_BY_FAMILY } from '../../../content';
import { resolveUnifiedSupport, FAMILY_SUPPORT_REGISTRY, type ResolvedSupport, type HintStep } from '../../../support';
interface FamilySupportRow {
  id: string;
  familyId: string;
  module: string;
  tier: number;
  questionCount: number;
  support: ResolvedSupport;
  completeness: number;
  hintCount: number;
  registryHints: HintStep[];
  inRegistry: boolean;
}
export function SupportContentPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [coverageFilter, setCoverageFilter] = useState<'all' | 'full' | 'partial' | 'none'>('all');
  const [hintFilter, setHintFilter] = useState<'all' | 'enriched' | 'sparse' | 'none'>('all');
  const [selectedFamily, setSelectedFamily] = useState<FamilySupportRow | null>(null);
  const allData: FamilySupportRow[] = useMemo(() => {
    return TAXONOMY.map(family => {
      const support = resolveUnifiedSupport(family.familyId);
      const registryEntry = FAMILY_SUPPORT_REGISTRY[family.familyId];
      const registryHints = registryEntry?.hints || [];
      return {
        id: family.familyId,
        familyId: family.familyId,
        module: family.module,
        tier: family.tier,
        questionCount: QUESTIONS_BY_FAMILY.get(family.familyId)?.length ?? 0,
        support,
        completeness: support.coverage.completeness,
        hintCount: registryHints.length,
        registryHints,
        inRegistry: !!registryEntry
      };
    });
  }, []);
  const stats = useMemo(() => {
    const total = allData.length;
    const inRegistry = allData.filter(d => d.inRegistry).length;
    const noHints = allData.filter(d => d.hintCount === 0).length;
    const totalHints = allData.reduce((sum, d) => sum + d.hintCount, 0);
    const overallCoverage = Math.round(inRegistry / total * 100);
    return {
      total,
      inRegistry,
      noHints,
      totalHints,
      overallCoverage
    };
  }, [allData]);
  const filteredData = useMemo(() => {
    return allData.filter(row => {
      if (searchQuery && !row.familyId.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (moduleFilter !== 'all' && row.module !== moduleFilter) return false;
      if (coverageFilter === 'full' && row.completeness !== 100) return false;
      if (coverageFilter === 'partial' && (row.completeness === 0 || row.completeness === 100)) return false;
      if (coverageFilter === 'none' && row.completeness !== 0) return false;
      if (hintFilter === 'enriched' && row.hintCount < 2) return false;
      if (hintFilter === 'sparse' && row.hintCount !== 1) return false;
      if (hintFilter === 'none' && row.hintCount !== 0) return false;
      return true;
    });
  }, [allData, searchQuery, moduleFilter, coverageFilter, hintFilter]);
  const modules = useMemo(() => Array.from(new Set(TAXONOMY.map(f => f.module))).sort(), []);
  const exampleQuestion = useMemo(() => {
    if (!selectedFamily) return null;
    const questions = QUESTIONS_BY_FAMILY.get(selectedFamily.familyId);
    return questions && questions.length > 0 ? questions[0] : null;
  }, [selectedFamily]);
  const renderCheck = (hasItem: boolean) => {
    return hasItem ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5401" /> : <XCircle className="w-4 h-4 text-gray-300" data-id="element-5402" />;
  };
  const renderHintCount = (count: number) => {
    if (count === 0) {
      return <span className="inline-flex items-center gap-1 text-xs text-gray-400" data-id="element-5403">
          <XCircle className="w-3.5 h-3.5" data-id="element-5404" />
          <span data-id="element-5405">0</span>
        </span>;
    }
    if (count === 1) {
      return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200" data-id="element-5406">
          <AlertTriangle className="w-3 h-3" data-id="element-5407" />
          <span data-id="element-5408">1</span>
        </span>;
    }
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200" data-id="element-5409">
        <Lightbulb className="w-3 h-3" data-id="element-5410" />
        <span data-id="element-5411">{count}</span>
      </span>;
  };
  const columns: Column<FamilySupportRow>[] = [{
    key: 'familyId',
    label: 'Family ID',
    render: val => <span className="font-mono text-xs font-medium text-gray-700" data-id="element-5412">
          {val}
        </span>
  }, {
    key: 'module',
    label: 'Domein',
    render: val => <Badge variant="secondary" data-id="element-5413">{val}</Badge>
  }, {
    key: 'tier',
    label: 'Tier',
    render: val => <Badge variant="secondary" data-id="element-5414">T{val}</Badge>
  }, {
    key: 'questionCount',
    label: 'Vragen',
    render: val => <span className="text-xs font-medium text-gray-600" data-id="element-5415">{val}</span>
  }, {
    key: 'explanation',
    label: 'Uitleg',
    render: (_, row) => renderCheck(row.support.coverage.hasExplanation)
  }, {
    key: 'cheatSheet',
    label: 'Spiekbriefje',
    render: (_, row) => renderCheck(row.support.coverage.hasCheatSheet)
  }, {
    key: 'hintCount',
    label: 'Hints',
    render: val => renderHintCount(val)
  }, {
    key: 'completeness',
    label: 'Compleetheid',
    render: val => <MasteryBar value={val} data-id="element-5416" />
  }];
  return <PageLayout title="Ondersteuning Content" subtitle="Uitleg, hints en spiekbriefjes per family — compleet overzicht van alle ondersteuningscontent." actions={<ConnectionBadge status="live" data-id="element-5418" />} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Ondersteuning'
  }]} data-id="element-5417">
      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-id="element-5419">
        <StatCard label="Dekking" value={`${stats.overallCoverage}%`} icon={<BookOpen className="w-5 h-5" data-id="element-5421" />} color={stats.overallCoverage === 100 ? 'emerald' : stats.overallCoverage >= 80 ? 'amber' : 'blue'} data-id="element-5420" />
        <StatCard label="In Register" value={stats.inRegistry} icon={<CheckCircle2 className="w-5 h-5" data-id="element-5423" />} color="emerald" data-id="element-5422" />
        <StatCard label="Totaal Hints" value={stats.totalHints} icon={<Lightbulb className="w-5 h-5" data-id="element-5425" />} color="blue" data-id="element-5424" />
        <StatCard label="Zonder Hints" value={stats.noHints} icon={<XCircle className="w-5 h-5" data-id="element-5427" />} color={stats.noHints > 0 ? 'red' : 'emerald'} data-id="element-5426" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6 bg-white p-4 rounded-xl border border-gray-200 shadow-sm items-center" data-id="element-5428">
        <div className="flex-1 w-full" data-id="element-5429">
          <Input type="text" placeholder="Zoek op Family ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-5431" />} variant="admin" size="sm" data-id="element-5430" />
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto" data-id="element-5432">
          <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500 font-medium px-2" data-id="element-5433">
            <Filter className="w-4 h-4" data-id="element-5434" />
            <span data-id="element-5435">Filters</span>
          </div>
          <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} options={[{
          value: 'all',
          label: 'Alle domeinen'
        }, ...modules.map(m => ({
          value: m,
          label: m
        }))]} variant="admin" size="sm" fullWidth={false} data-id="element-5436" />
          <Select value={coverageFilter} onChange={e => setCoverageFilter(e.target.value as typeof coverageFilter)} options={[{
          value: 'all',
          label: 'Alle dekkingen'
        }, {
          value: 'full',
          label: 'Volledig (100%)'
        }, {
          value: 'partial',
          label: 'Gedeeltelijk (1-99%)'
        }, {
          value: 'none',
          label: 'Geen (0%)'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-5437" />
          <Select value={hintFilter} onChange={e => setHintFilter(e.target.value as typeof hintFilter)} options={[{
          value: 'all',
          label: 'Alle hints'
        }, {
          value: 'enriched',
          label: 'Verrijkt (≥2)'
        }, {
          value: 'sparse',
          label: 'Schaars (1)'
        }, {
          value: 'none',
          label: 'Geen hints'
        }]} variant="admin" size="sm" fullWidth={false} data-id="element-5438" />
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-gray-500 font-medium" data-id="element-5439">
        {filteredData.length} van {allData.length} families
      </div>

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-5440">
        {/* Table */}
        <div className={`flex-1 transition-all ${selectedFamily ? 'lg:w-2/3' : 'w-full'}`} data-id="element-5441">
          <DataTable columns={columns} data={filteredData} onRowClick={setSelectedFamily} emptyMessage="Geen families gevonden met deze filters." data-id="element-5442" />
        </div>

        {/* Preview Panel */}
        {selectedFamily && <div className="w-full lg:w-1/3 flex-shrink-0" data-id="element-5443">
            <Card className="sticky top-6 flex flex-col max-h-[calc(100vh-120px)] shadow-sm border-gray-200" data-id="element-5444">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/80 rounded-t-xl" data-id="element-5445">
                <div data-id="element-5446">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-5447">
                    <FileText className="w-4 h-4 text-blue-500" data-id="element-5448" />
                    Content Preview
                  </h3>
                  <p className="text-xs font-mono text-gray-500 mt-1" data-id="element-5449">
                    {selectedFamily.familyId}
                  </p>
                </div>
                <button onClick={() => setSelectedFamily(null)} className="p-1.5 h-auto text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors" data-id="element-5450">
                  <X className="w-4 h-4" data-id="element-5451" />
                </button>
              </div>

              <div className="p-5 overflow-y-auto flex-1 space-y-6" data-id="element-5452">
                {/* Registry status */}
                <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${selectedFamily.inRegistry ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-id="element-5453">
                  {selectedFamily.inRegistry ? <>
                      <CheckCircle2 className="w-4 h-4" data-id="element-5454" /> In
                      FAMILY_SUPPORT_REGISTRY
                    </> : <>
                      <XCircle className="w-4 h-4" data-id="element-5455" /> Niet in register — geen
                      support content
                    </>}
                </div>

                {/* Family Context */}
                <div className="space-y-3" data-id="element-5456">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-5457">
                    Context
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-5458">
                    <Badge variant="secondary" data-id="element-5459">
                      Tier {selectedFamily.tier}
                    </Badge>
                    <Badge variant="secondary" data-id="element-5460">
                      {selectedFamily.questionCount} Vragen
                    </Badge>
                    <Badge variant="secondary" data-id="element-5461">{selectedFamily.module}</Badge>
                  </div>
                </div>

                {/* Example Question */}
                {exampleQuestion && <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-5462">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-5463">
                      Voorbeeld Vraag
                    </h4>
                    <div className="bg-gray-50 p-3.5 rounded-lg border border-gray-200" data-id="element-5464">
                      <p className="text-sm text-gray-800 leading-relaxed mb-3" data-id="element-5465">
                        {exampleQuestion.question}
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-5466">
                        <Badge variant="secondary" data-id="element-5467">
                          {exampleQuestion.questionType}
                        </Badge>
                        <Badge variant="secondary" data-id="element-5468">
                          {exampleQuestion.levelCode}
                        </Badge>
                      </div>
                    </div>
                  </div>}

                {/* Explanation */}
                <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-5469">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2" data-id="element-5470">
                    {selectedFamily.support.explanation?.body ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5471" /> : <XCircle className="w-4 h-4 text-gray-300" data-id="element-5472" />}{' '}
                    Meester Djay legt uit
                  </h4>
                  {selectedFamily.support.explanation?.body ? <div className="bg-orange-50/50 p-4 rounded-lg border border-orange-100" data-id="element-5473">
                      {(() => {
                  const body = selectedFamily.support.explanation.body;
                  const lines = body.split('\n').filter((l: string) => l.trim().length > 0);
                  if (lines.length <= 1) {
                    return <p className="text-sm text-orange-800 whitespace-pre-wrap" data-id="element-5474">
                              {body}
                            </p>;
                  }
                  return <ol className="list-decimal list-inside space-y-1.5" data-id="element-5475">
                            {lines.map((line: string, i: number) => <li key={i} className="text-sm text-orange-800" data-id="element-5476">
                                {line.trim()}
                              </li>)}
                          </ol>;
                })()}
                      <p className="text-xs text-orange-400/80 mt-3 italic" data-id="element-5477">
                        {(() => {
                    const lines = selectedFamily.support.explanation.body.split('\n').filter((l: string) => l.trim().length > 0);
                    return lines.length > 1 ? `${lines.length} stappen (afgeleid uit howItWorks newlines)` : 'Enkele tekst (geen stappen)';
                  })()}
                      </p>
                    </div> : <p className="text-sm text-gray-400 italic bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-5478">
                      Geen uitleg beschikbaar — howItWorks ontbreekt.
                    </p>}
                  {selectedFamily.support.steps.length > 0 && <div className="mt-3 bg-gray-50 p-3 rounded-lg border border-gray-200" data-id="element-5479">
                      <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1.5" data-id="element-5480">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" data-id="element-5481" />
                        Legacy steps[] (deprecated v4.3)
                      </p>
                      <ol className="list-decimal list-inside space-y-1" data-id="element-5482">
                        {selectedFamily.support.steps.map((step: string, i: number) => <li key={i} className="text-xs text-gray-500" data-id="element-5483">
                              {step}
                            </li>)}
                      </ol>
                    </div>}
                </div>

                {/* Hints */}
                <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-5484">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2" data-id="element-5485">
                    {renderHintCount(selectedFamily.hintCount)} Hints (
                    {selectedFamily.hintCount})
                  </h4>
                  {selectedFamily.registryHints.length > 0 ? <div className="space-y-3" data-id="element-5486">
                      {selectedFamily.registryHints.map((hint, i) => <div key={i} className="bg-amber-50/50 p-3.5 rounded-lg border border-amber-100" data-id="element-5487">
                          {hint.title && <p className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-1.5" data-id="element-5488">
                              {hint.title}
                            </p>}
                          <p className="text-sm text-amber-900 whitespace-pre-wrap" data-id="element-5489">
                            {hint.content}
                          </p>
                          {hint.visual && <div className="mt-2 pt-2 border-t border-amber-200/50 flex items-center gap-1.5" data-id="element-5490">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" data-id="element-5491" />
                              <p className="text-xs font-medium text-amber-700" data-id="element-5492">
                                Visueel: {hint.visual.type}
                              </p>
                            </div>}
                        </div>)}
                    </div> : <p className="text-sm text-gray-400 italic bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-5493">
                      Geen hints beschikbaar.
                    </p>}
                </div>

                {/* Cheat Sheet */}
                <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-5494">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2" data-id="element-5495">
                    {renderCheck(selectedFamily.support.coverage.hasCheatSheet)}{' '}
                    Spiekbriefje
                  </h4>
                  {selectedFamily.support.cheatSheet ? <div className="bg-emerald-50/50 p-4 rounded-lg border border-emerald-100" data-id="element-5496">
                      <p className="text-sm font-bold text-emerald-900 mb-2" data-id="element-5497">
                        {selectedFamily.support.cheatSheet.title}
                      </p>
                      <p className="text-sm text-emerald-800 whitespace-pre-wrap leading-relaxed" data-id="element-5498">
                        {selectedFamily.support.cheatSheet.body}
                      </p>
                      {selectedFamily.support.cheatSheet.rules && selectedFamily.support.cheatSheet.rules.length > 0 && <ul className="list-disc list-inside mt-3 space-y-1 text-sm text-emerald-800" data-id="element-5499">
                            {selectedFamily.support.cheatSheet.rules.map((rule, i) => <li key={i} data-id="element-5500">{rule}</li>)}
                          </ul>}
                    </div> : <p className="text-sm text-gray-400 italic bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-5501">
                      Geen spiekbriefje beschikbaar.
                    </p>}
                </div>

                {/* Session Summary */}
                <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-5502">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2" data-id="element-5503">
                    {renderCheck(selectedFamily.support.coverage.hasAfterSummary)}{' '}
                    Sessie Samenvatting
                  </h4>
                  {selectedFamily.support.afterSummary ? <div className="bg-purple-50/50 p-3.5 rounded-lg border border-purple-100" data-id="element-5504">
                      <p className="text-sm text-purple-900 leading-relaxed" data-id="element-5505">
                        {selectedFamily.support.afterSummary}
                      </p>
                    </div> : <p className="text-sm text-gray-400 italic bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-5506">
                      Geen samenvatting beschikbaar.
                    </p>}
                </div>
              </div>
            </Card>
          </div>}
      </div>
    </PageLayout>;
}